import React, { ChangeEvent, useState, useEffect } from "react";
import { SelectChangeEvent, TextField, Autocomplete, FormControlLabel, Checkbox, MenuItem, Select, Button, CardMedia, IconButton, Input, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";
import { useGetUsers } from "../../api/hooks/useUser";
import { useSendAdminNotification } from "../../api/hooks/useNotification";
import { NOTIFICATION_TYPE } from "../../api/types/notification.types";
import type { User } from "../../api/types/user.types";

const ALL_USERS_OPTION = { _id: 'all', fullName: 'Select All Users', email: '' };

const AddNotification = () => {
  const navigate = useNavigate();

  // Form State
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState<number | 'default'>('default');
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  // Search State for Users
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserSearch(userSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const { data: usersData, isLoading: isLoadingUsers } = useGetUsers({
    search: debouncedUserSearch,
    page: 1,
    limit: 100,
  });

  const usersList = (usersData?.data as any)?.list || [];
  const options = [ALL_USERS_OPTION, ...usersList];

  const { mutate: sendNotification, isPending } = useSendAdminNotification({
    onSuccess: () => {
      toast.success("Notification sent successfully!");
      navigate('/manage-notifications');
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to send notification");
    }
  });

  const handleUsersChange = (event: any, value: any[]) => {
    const isSelectAllClicked = value.some((user) => user._id === 'all');

    if (isSelectAllClicked) {
      // If "Select All" is clicked, we just keep "Select All" as the only selected item for UI logic
      // Or we can toggle it. Let's follow the user's logic: if all selected, unselect all.
      const isSystemAlreadySelectAll = selectedUsers.some(u => u._id === 'all');

      if (isSystemAlreadySelectAll) {
        setSelectedUsers([]);
      } else {
        setSelectedUsers([ALL_USERS_OPTION]);
      }
    } else {
      setSelectedUsers(value.filter(u => u._id !== 'all'));
    }
  };

  const handleTypeChange = (event: SelectChangeEvent<number | 'default'>) => {
    setType(event.target.value as number);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !message || type === 'default') {
      toast.error("Please fill in all required fields.");
      return;
    }

    const isSendToAll = selectedUsers.some(u => u._id === 'all');

    if (!isSendToAll && selectedUsers.length === 0) {
      toast.error("Please select at least one user or Select All.");
      return;
    }

    sendNotification({
      title,
      message,
      type: type as any,
      isSendToAll,
      broadcastReceivers: isSendToAll ? [] : selectedUsers.map(u => u._id)
    });
  };

  return (
    <>
      <div className="main_title">
        <h1>Add Notification</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - {" "}
          <button onClick={() => navigate("/manage-notifications")}>Notification & Communication Center</button> -
          Add Notification
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="noti_title">Notification Title</label>
              <TextField
                id="noti_title"
                placeholder="Enter Notification Title"
                fullWidth
                hiddenLabel
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="control_group w_50">
              <label htmlFor="noti_type">Type</label>
              <Select
                id="noti_type"
                fullWidth
                value={type}
                onChange={handleTypeChange}
              >
                <MenuItem disabled value="default">Select Type</MenuItem>
                <MenuItem value={NOTIFICATION_TYPE.BROADCAST}>Broadcast</MenuItem>
                <MenuItem value={NOTIFICATION_TYPE.MAIL}>Email</MenuItem>
              </Select>
            </div>
            <div className="control_group w_100">
              <label htmlFor="noti_audience">Audience</label>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={options}
                value={selectedUsers}
                onChange={handleUsersChange}
                getOptionLabel={(option) =>
                  option._id === 'all'
                    ? option.fullName
                    : `${option.fullName}${option.email ? `, ${option.email}` : ''}`
                }
                isOptionEqualToValue={(option, value) => option._id === value._id}
                onInputChange={(e, val) => setUserSearch(val)}
                loading={isLoadingUsers}
                renderOption={(props, option, { selected }) => {
                  const isAllOption = option._id === 'all';
                  const isAllSelected = selectedUsers.some(u => u._id === 'all');
                  return (
                    <li {...props}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isAllOption ? isAllSelected : selected}
                          />
                        }
                        name="option"
                        label={option.fullName + (option.email ? `, ${option.email}` : '')}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="noti_audience"
                    placeholder="Select Audience"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </div>

            <div className="control_group w_100">
              <label htmlFor="noti_message">Notification Message</label>
              <TextField
                id="noti_message"
                placeholder="Enter Notification Message"
                fullWidth
                hiddenLabel
                multiline
                maxRows={5}
                minRows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

          </div>
          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary"
              disabled={isPending}
            >
              {isPending ? "Sending..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddNotification;
